import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Switch,
  FormControlLabel,
  Paper,
  Divider,
} from '@mui/material';
import {
  Check as CheckIcon,
  Star as StarIcon,
  VideoLibrary as VideoIcon,
  Analytics as AnalyticsIcon,
  Schedule as ScheduleIcon,
  Settings as SettingsIcon,
  Api as ApiIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription, plans } from '../contexts/SubscriptionContext';
import api from '../services/api';
import Header from '../components/Header/Header';
import Footer from '../components/Footer/Footer';

interface PricingTier {
  name: string;
  price: number;
  period: string;
  description: string;
  features: string[];
  reelsLimit: number;
  popular?: boolean;
  buttonText: string;
  buttonColor: 'primary' | 'secondary';
}

const pricingTiers: PricingTier[] = [
  {
    name: 'Basic',
    price: 39, // Increased from $29
    period: 'month',
    description: 'Perfect for content creators getting started',
    reelsLimit: 30, // Reduced from 50
    features: [
      '30 reels per month',
      'Managed APIs (no setup required)',
      'Auto-scheduling',
      'Basic analytics',
      'Email support',
      'HD video export',
      'Usage alerts at 80%',
    ],
    buttonText: 'Start Basic Plan',
    buttonColor: 'primary',
  },
  {
    name: 'Pro',
    price: 149, // Increased from $99
    period: 'month',
    description: 'For serious content creators and small teams',
    reelsLimit: 100, // Reduced from 200
    popular: true,
    features: [
      '100 reels per month',
      'Managed APIs (no setup required)',
      'Advanced scheduling',
      'Detailed analytics & insights',
      'Priority support',
      '4K video export',
      'Custom branding',
      'Batch processing',
      'Usage alerts & cost monitoring',
    ],
    buttonText: 'Start Pro Plan',
    buttonColor: 'primary',
  },
  {
    name: 'Enterprise',
    price: 399, // Increased from $299
    period: 'month',
    description: 'For agencies and large teams',
    reelsLimit: 200, // Changed from unlimited
    features: [
      '200 reels per month',
      'Managed APIs (no setup required)',
      'Team collaboration',
      'Advanced analytics & reporting',
      'Dedicated account manager',
      '4K video export',
      'White-label solution',
      'API access',
      'Custom integrations',
      'Overage protection',
    ],
    buttonText: 'Contact Sales',
    buttonColor: 'secondary',
  },
];

const Pricing: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { subscription } = useSubscription();
  const [showByoaAddon, setShowByoaAddon] = useState(false);
  const [loading, setLoading] = useState<string | null>(null);

  const handlePlanSelect = async (tier: PricingTier) => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (tier.name === 'Enterprise') {
      // Open contact form or redirect to sales
      window.open('mailto:sales@autoreelr.com?subject=Enterprise Plan Inquiry', '_blank');
      return;
    }

    try {
      setLoading(tier.name);
      
      // Create Stripe checkout session
      const response = await api.post('/stripe/create-checkout-session', {
        plan: tier.name.toLowerCase(),
        hasByoaAddon: showByoaAddon,
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
      
    } catch (error) {
      console.error('Failed to create checkout session:', error);
      setLoading(null);
      // TODO: Show error message to user
    }
  };

  return (
    <Box>
      <Header />
      <Container maxWidth="lg">
        <Box py={8}>
        {/* Header */}
        <Box textAlign="center" mb={6}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
              Simple, Transparent Pricing
            </Typography>
            <Typography variant="h5" color="text.secondary" paragraph>
              Choose the plan that fits your content creation needs
            </Typography>
          </motion.div>
        </Box>

        {/* BYOA Toggle */}
        <Paper sx={{ p: 3, mb: 4, textAlign: 'center' }}>
          <Box display="flex" alignItems="center" justifyContent="center" gap={2}>
            <Typography variant="h6">Pricing Options:</Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={showByoaAddon}
                  onChange={(e) => setShowByoaAddon(e.target.checked)}
                  color="primary"
                />
              }
              label="Show Bring Your Own API Add-on"
            />
          </Box>
          {showByoaAddon && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              transition={{ duration: 0.3 }}
            >
              <Box mt={2}>
                <Chip
                  icon={<ApiIcon />}
                  label="BYOA Add-on: Save $10-$50/month (Bring Your Own API keys)"
                  color="secondary"
                  variant="outlined"
                  sx={{ fontSize: '1rem', p: 2 }}
                />
              </Box>
            </motion.div>
          )}
        </Paper>

        {/* Pricing Cards */}
        <Grid container spacing={4} justifyContent="center">
          {pricingTiers.map((tier, index) => (
            <Grid item xs={12} md={4} key={tier.name}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -8 }}
                style={{ height: '100%' }}
              >
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    border: tier.popular ? '2px solid' : '1px solid',
                    borderColor: tier.popular ? 'primary.main' : 'divider',
                    boxShadow: tier.popular ? '0 8px 32px rgba(102, 126, 234, 0.2)' : undefined,
                  }}
                >
                  {tier.popular && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -12,
                        left: '50%',
                        transform: 'translateX(-50%)',
                      }}
                    >
                      <Chip
                        icon={<StarIcon />}
                        label="Most Popular"
                        color="primary"
                        sx={{ fontWeight: 600 }}
                      />
                    </Box>
                  )}

                  <CardContent sx={{ flexGrow: 1, p: 4 }}>
                    {/* Plan Header */}
                    <Box textAlign="center" mb={3}>
                      <Typography variant="h4" component="h3" gutterBottom sx={{ fontWeight: 600 }}>
                        {tier.name}
                      </Typography>
                      <Typography variant="body1" color="text.secondary" paragraph>
                        {tier.description}
                      </Typography>
                      <Box display="flex" alignItems="baseline" justifyContent="center" gap={1}>
                        <Typography variant="h2" component="span" sx={{ fontWeight: 700 }}>
                          ${showByoaAddon && plans[tier.name.toLowerCase() as keyof typeof plans]?.hasByoaOption 
                            ? tier.price - (plans[tier.name.toLowerCase() as keyof typeof plans]?.byoaDiscount || 0)
                            : tier.price}
                        </Typography>
                        <Typography variant="h6" color="text.secondary">
                          /{tier.period}
                        </Typography>
                      </Box>
                      {showByoaAddon && plans[tier.name.toLowerCase() as keyof typeof plans]?.hasByoaOption && (
                        <Typography variant="caption" color="secondary" display="block" mt={1}>
                          With BYOA add-on (-${plans[tier.name.toLowerCase() as keyof typeof plans]?.byoaDiscount}/month)
                        </Typography>
                      )}
                    </Box>

                    {/* Features List */}
                    <List dense>
                      {tier.features.map((feature, featureIndex) => (
                        <ListItem key={featureIndex} disablePadding>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <CheckIcon color="primary" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={feature}
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                      ))}
                      {showByoaAddon && plans[tier.name.toLowerCase() as keyof typeof plans]?.hasByoaOption && (
                        <ListItem disablePadding>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <ApiIcon color="secondary" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={`Bring Your Own API keys (-$${plans[tier.name.toLowerCase() as keyof typeof plans]?.byoaDiscount}/month)`}
                            primaryTypographyProps={{ 
                              variant: 'body2',
                              color: 'secondary.main',
                              fontWeight: 600
                            }}
                          />
                        </ListItem>
                      )}
                    </List>

                    {/* CTA Button */}
                    <Box mt={4}>
                      <Button
                        variant="contained"
                        color={tier.buttonColor}
                        size="large"
                        fullWidth
                        onClick={() => handlePlanSelect(tier)}
                        disabled={loading === tier.name}
                        sx={{
                          py: 1.5,
                          fontSize: '1.1rem',
                          fontWeight: 600,
                          ...(tier.popular && {
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #7788ff 0%, #8659b8 100%)',
                            },
                          }),
                        }}
                      >
                        {loading === tier.name ? 'Processing...' : tier.buttonText}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        {/* Feature Comparison */}
        <Box mt={8}>
          <Typography variant="h4" textAlign="center" gutterBottom sx={{ fontWeight: 600 }}>
            What's Included
          </Typography>
          <Grid container spacing={4} mt={2}>
            {[
              {
                icon: <VideoIcon />,
                title: 'AI Video Generation',
                description: 'Create stunning reels with AI-powered video generation',
              },
              {
                icon: <AnalyticsIcon />,
                title: 'Performance Analytics',
                description: 'Track engagement, views, and optimize your content strategy',
              },
              {
                icon: <ScheduleIcon />,
                title: 'Smart Scheduling',
                description: 'Post at optimal times across all your social platforms',
              },
              {
                icon: <SettingsIcon />,
                title: 'Easy Management',
                description: 'Organize projects, collaborate with teams, and streamline workflows',
              },
            ].map((feature, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Box textAlign="center">
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                      color: 'primary.main',
                      display: 'inline-flex',
                      mb: 2,
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Typography variant="h6" gutterBottom>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* FAQ Section */}
        <Box mt={8} textAlign="center">
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
            Questions?
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Need help choosing the right plan? Our team is here to help.
          </Typography>
          <Button
            variant="outlined"
            size="large"
            onClick={() => navigate('/contact')}
            sx={{ mt: 2 }}
          >
            Contact Sales
          </Button>
        </Box>
      </Box>
    </Container>
    <Footer />
  </Box>
  );
};

export default Pricing;